import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { MFChannel } from '../../Components/SDK';
import { getAppOnlineStatus } from '../../slice';
import { DefaultChannel, SearchBox, SmartListView } from '../components';
import { loaderGIF } from '../rootAccess';
import { showErrorToast, showSucessToast, useInvitationList } from '../utils';
import logger from '../../Helpers/logger';
import InvitationItem from './InvitationItem';
import './InviteList.scss';

const Invitiations = ({ _handleChannelsClick }) => {
    const isOnline = useSelector(getAppOnlineStatus);
    const prevIsOnline = useRef(isOnline);
    const [processingActivityIds, setProcessingActivityIds] = useState(new Set());
    const { invitations, loading, hasMore, loadNextPage, updateSearchText, searchText, refresh } = useInvitationList({
        pageSize: 20,
    });

    // 🧠 Memoize transformed list
    const inviteList = useMemo(() => {
        return invitations.map(({ initiatedBy, channelDetails, createdAt, status, activityId, updatedBy }) => ({
            activityId: activityId,
            channelId: channelDetails?.channelId,
            image: channelDetails?.imageUri || '',
            name: channelDetails?.name || 'Untitled Channel',
            status: status || 'PENDING',
            approved: status === 'APPROVED',
            declined: status === 'DECLINED',
            initiatedBy,
            updatedBy,
            createdAt,
        }));
    }, [invitations]);

    useEffect(() => {
        if (!prevIsOnline.current && isOnline) {
            refresh();
        }
        prevIsOnline.current = isOnline;
    }, [isOnline, refresh]);

    const handleApprove = useCallback(
        async item => {
            if (processingActivityIds.has(item.activityId)) return;

            setProcessingActivityIds(prev => new Set(prev).add(item.activityId));
            try {
                const res = await MFChannel.acceptOrDecline({
                    channelId: item.channelId,
                    activityId: item.activityId,
                    status: 'APPROVED',
                });

                if (res.statusCode !== 200) {
                    showErrorToast(res?.message || 'Failed to approve the request');
                    setProcessingActivityIds(prev => {
                        const next = new Set(prev);
                        next.delete(item.activityId);
                        return next;
                    });
                    return;
                }

                showSucessToast('User approved successfully');
            } catch (err) {
                logger.error('❌ Failed to approve the request', err);
                showErrorToast('Network error occurred. Please try again.');
            } finally {
                setProcessingActivityIds(prev => {
                    const next = new Set(prev);
                    next.delete(item.activityId);
                    return next;
                });
            }
        },
        [processingActivityIds],
    );

    const handleDecline = useCallback(
        async item => {
            if (processingActivityIds.has(item.activityId)) return;

            setProcessingActivityIds(prev => new Set(prev).add(item.activityId));
            try {
                const res = await MFChannel.acceptOrDecline({
                    channelId: item.channelId,
                    activityId: item.activityId,
                    status: 'DECLINED',
                });

                if (res.statusCode !== 200) {
                    showErrorToast(res?.message || 'Failed to decline the request');
                    setProcessingActivityIds(prev => {
                        const next = new Set(prev);
                        next.delete(item.activityId);
                        return next;
                    });
                    return;
                }

                showSucessToast('User declined successfully');
            } catch (err) {
                logger.error('❌ Failed to decline the request', err);
                showErrorToast('Network error occurred. Please try again.');
            } finally {
                setProcessingActivityIds(prev => {
                    const next = new Set(prev);
                    next.delete(item.activityId);
                    return next;
                });
            }
        },
        [processingActivityIds],
    );

    // 🧠 Memoize item renderer
    const renderItem = useCallback(
        ({ item }) => {
            const isProcessing = processingActivityIds.has(item.activityId);
            return (
                <InvitationItem
                    item={item}
                    isProcessing={isProcessing}
                    onApprove={handleApprove}
                    onDecline={handleDecline}
                />
            );
        },
        [handleApprove, handleDecline, processingActivityIds],
    );

    const renderEmptyView = () => {
        const isDisconnected = !isOnline;
        const isSearching = !!searchText;

        if (isDisconnected) {
            return (
                <DefaultChannel
                    Title="No internet connection"
                    Description=""
                    btnText=""
                    btnClick={undefined}
                    Image=""
                    alt=""
                />
            );
        }

        const isSearchingResult = isSearching;
        const shouldShowDefault = !isSearchingResult;

        return (
            // <DefaultChannel
            //    Title={isSearchingResult ? 'No results found' : 'Engage with your People'}
            //    Description={
            //       shouldShowDefault
            //          ? 'The community feature connects users for interaction and collaboration, fostering engagement through discussions and updates.'
            //          : ''
            //    }
            //    btnText={shouldShowDefault ? 'Create Channel' : ''}
            //    btnClick={
            //       shouldShowDefault
            //          ? () => {
            //               handleChannelsClick?.();
            //               handleChannelNavigation({ pane: 'createChannel' });
            //            }
            //          : undefined
            //    }
            //    Image={shouldShowDefault ? ImageCreateChannel : ''}
            //    alt={shouldShowDefault ? 'Create Channel Icon' : ''}
            // />
            <div className="no_requests">
                {isSearchingResult ? 'No results found' : 'There are no requests to be viewed'}
            </div>
        );
    };

    const renderLoader = useCallback(
        () => (
            <div style={{ textAlign: 'center' }}>
                <img src={loaderGIF} alt="Loading" style={{ width: '40px', height: '40px' }} />
            </div>
        ),
        [],
    );

    return (
        <>
            <SearchBox onChange={updateSearchText} />

            <div className="invite-list chat-list">
                <SmartListView
                    renderItem={renderItem}
                    data={inviteList}
                    onLoadBottom={loadNextPage}
                    hasMoreBottom={hasMore}
                    ListFooterComponent={loading ? renderLoader() : null}
                    ListEmptyComponent={!loading && renderEmptyView()}
                />
            </div>
        </>
    );
};

export default Invitiations;

Invitiations.propTypes = {
    handleChannelsClick: PropTypes.func,
};
